import { NgTemplateOutlet } from '@angular/common';
import { Component, Injector, ModelSignal, OutputEmitterRef, TemplateRef, WritableSignal, computed, inject, input, model, output, signal, viewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import { TranslateDirective } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';

import { Breakpoint, BreakpointState, MediaQueryService } from '../../services/media-query.service';
import { BackendError } from '../../domain/error.domain';
import { MarketType, MarketTypeHelper } from '../../domain/market.domain';
import { SecuritySymbol, SecurityInfo, SecuritySymbolHelper, SymbolType } from '../../domain/symbol.domain';
import { AutoSuggestFilterBy, AutoSuggestSymbol, SymbolLookupDataSource } from '../../domain/symbol-lookup.domain';
import { SignalFormBridgeService } from '../../components/form/interop/signal-form-bridge.service';
import { ValueAccessorProviderFactory } from '../../components/value-accessor/value-accessor-provider-factory';
import { NewTypeAheadComponent } from '../../components/typeahead/new-typeahead.component';
import { DropdownItemComponent } from '../../components/dropdown/dropdown-item.component';
import { IconButtonComponent } from '../../components/icon-button/icon-button.component';
import { IconComponent } from '../../components/icon/icon.component';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { resolveSymbolLookupModelValue } from './symbol-lookup-model-resolver';
import { shouldAutoSelectOnFocusOut } from './symbol-lookup-focusout-policy';
import { planSymbolLookupButtonClick, planSymbolLookupInputChange, planSymbolLookupSelection, type SymbolLookupInputModelEffect } from './symbol-lookup-orchestrator';
import { shouldOpenRecentSearchDropdown } from './symbol-lookup-recent-search-resolver';
import { SymbolLookupItemLabelComponent } from './symbol-lookup-item-label.component';
import { resolveSymbolLookupSearch } from './symbol-lookup-search-resolver';
import { SymbolLookupService } from './symbol-lookup.service';
import { SymbolValidator } from './symbol-validator';

@Component({
  selector: 'td-wb-symbol-lookup',
  templateUrl: 'symbol-lookup.component.html',
  styleUrls: ['symbol-lookup.component.scss'],
  providers: [ValueAccessorProviderFactory(SymbolLookupComponent)],
  imports: [
    NewTypeAheadComponent,
    TranslatePipe,
    TranslateDirective,
    FormsModule,
    DropdownItemComponent,
    SymbolLookupItemLabelComponent,
    NgTemplateOutlet,
    IconButtonComponent,
    IconComponent,
    CdkOverlayOrigin
  ],
  host: {
    '[class.td-wb-symbol-lookup--hover]': 'hoverState()',
    '[class.td-wb-symbol-lookup--focus]': 'focusState()',
    '(focusout)': 'onFocusOut($event)',
    '(focusin)': 'onFocus()',
    '(mouseenter)': 'onHover()',
    '(mouseleave)': 'onHoverOut()'
  }
})
export class SymbolLookupComponent<T extends AutoSuggestSymbol> implements ControlValueAccessor {
  private readonly defaultDataSource = inject<SymbolLookupDataSource>(SymbolLookupService);
  private readonly bridgeService = inject(SignalFormBridgeService);
  private readonly injector = inject(Injector);
  private readonly mediaQueryService = inject(MediaQueryService);

  private static readonly MAX_RESULTS_DEFAULT: number = 7;
  private static readonly MAX_RESULTS_COMPACT: number = 5;
  readonly name = input<string>(undefined);
  readonly lenient = input<boolean>(undefined);
  readonly maxResults = input<number>(SymbolLookupComponent.MAX_RESULTS_DEFAULT);
  readonly maxResultsCompact = input<number>(SymbolLookupComponent.MAX_RESULTS_COMPACT);
  readonly market = input<MarketType, string>(undefined, { transform: (value: string): MarketType => MarketTypeHelper.fromString(value) });
  readonly symbolTypes = input<SymbolType | Array<SymbolType>>(undefined);
  /**
   * Declared `disabled` input. Never read this directly for gating — read {@link disabled},
   * which reconciles it with the owner-driven state written by `setDisabledState()`.
   */
  readonly disabledInput = input<boolean>(false, { alias: 'disabled' });
  /** Owner-driven disabled state, written only by `setDisabledState()`. */
  private readonly disabledByOwner: WritableSignal<boolean> = signal(false);
  /**
   * Single reconciled disabled state.
   *
   * Angular Forms (all three models) writes the `disabled` input AND calls
   * `setDisabledState()`; neither alone is authoritative. See §4.2 criterion 3 of
   * `wiki/signal-forms/core-signal-forms-revised-dual-support-plan.md`.
   */
  readonly disabled = computed((): boolean => this.disabledInput() || this.disabledByOwner());
  readonly debounce = input<number>(300);
  readonly dataSource = input<SymbolLookupDataSource>(undefined);
  readonly placeholderKey = input<string>(undefined);
  readonly customTemplate = input<TemplateRef<unknown>>(undefined);
  readonly preSelectFirstItem = input<boolean>(true);
  readonly recentSymbolSearch = input<boolean>(false);
  readonly fractionalSymbolSearch = input<boolean>(false);
  readonly filterBy = input<AutoSuggestFilterBy | undefined>(undefined);

  readonly onSelect = output<SecurityInfo>();
  readonly onInputChange = output<string>();
  readonly onHoverOrActive = output<{
    hoverState: boolean;
    focusState: boolean;
}>();

  readonly hoverState: WritableSignal<boolean> = signal(false);
  readonly focusState: WritableSignal<boolean> = signal(false);
  readonly typeAhead = viewChild(NewTypeAheadComponent);
  readonly focusOnInput: WritableSignal<boolean> = signal(false);
  readonly checkSymbol: WritableSignal<boolean> = signal(false);
  readonly symbolInput: WritableSignal<string> = signal(undefined);
  /**
   * Single source of truth, and the Signal Forms native custom-control binding.
   *
   * Empty is represented as `null`, never `undefined`: writing `undefined` into a Signal
   * Forms model deletes the backing property and orphans the field (NG01902). See hazard
   * H3 / §4.2 criterion 5 of
   * `wiki/signal-forms/core-signal-forms-revised-dual-support-plan.md`.
   */
  readonly value: ModelSignal<T | null> = model<T | null>(null);

  /** Signal Forms native custom-control touch notification. */
  readonly touch: OutputEmitterRef<void> = output<void>();
  readonly symbols: WritableSignal<Array<AutoSuggestSymbol>> = signal([]);
  readonly compactSymbolLabel: WritableSignal<boolean> = signal(false);
  readonly isRecentSearch: WritableSignal<boolean> = signal(false);
  readonly activeState = computed(() => this.hoverState() || this.focusState());
  readonly showRecentSearchState = computed(() => this.isRecentSearch() && this.recentSymbolSearch() && this.isEmptyInputValue(this.symbolInput()));
  private readonly symbolInputValueChange: Subject<string> = new Subject<string>();
  private onChangeCallback: (value: unknown) => void = (): void => {};
  private onTouchedCallback: () => void = (): void => {};
  private ngControlRef: NgControl | null = null;
  private isNgControlResolved: boolean = false;
  private currentMaxResult: number = SymbolLookupComponent.MAX_RESULTS_DEFAULT;

  constructor() {
    this.setupSymbolInputValueChangeObserver();

    this.mediaQueryService.observe(Breakpoint.SM)
      .pipe(takeUntilDestroyed())
      .subscribe((breakpointState: BreakpointState): void => {
        // assuming maxResultsCompact and maxResults will never change after component has been initialized
        this.currentMaxResult = breakpointState.matches ? this.maxResultsCompact() : this.maxResults();
        this.compactSymbolLabel.set(breakpointState.matches);
      });
  }

  onFocusOut(event: FocusEvent): void {
    if (this.shouldAutoSelectOnFocusOut(event)) {
      this.onSymbolButtonClicked();
    }
    this.setFocusState(false);
  }

  onFocus(): void {
    this.setFocusState(true);
  }

  onHover(): void {
    this.setHoverState(true);
  }

  onHoverOut(): void {
    this.setHoverState(false);
  }

  isActive(): boolean {
    return this.activeState();
  }

  querySymbols(symbolQuery: string): void {
    if (symbolQuery === undefined) {
      this.symbols.set([]);
    } else {
      this.symbolInputValueChange.next(symbolQuery);
    }
  }

  setRecentSearches(inputValue: string): void {
    this.focusOnInput.set(true);
    if (!(this.isEmptyInputValue(inputValue) && this.recentSymbolSearch())) {
      return;
    }

    this.getRecentSearches().subscribe({
      next: (result: Array<T>): void => {
        if (result.length === 0) {
          return;
        }

        this.symbols.set(result);
        this.isRecentSearch.set(true);

        const typeAhead = this.typeAhead();
        if (shouldOpenRecentSearchDropdown({
          isOpen: typeAhead.isOpen(),
          focusOnInput: this.focusOnInput(),
          isSkipOpenOnFocus: typeAhead.isSkipOpenOnFocus(),
          symbolInput: this.symbolInput()
        })) {
          typeAhead.open();
        }
      },
      error: (errorResponse: Array<BackendError>): void => {
        if (errorResponse) {
          this.symbols.set([]);
        }
      }
    });
  }

  inputChange(inputValue: string): void {
    if (this.disabled()) {
      return;
    }

    this.symbolInput.set(inputValue);

    const inputPlan = planSymbolLookupInputChange({
      inputValue,
      lenient: this.lenient(),
      checkSymbol: this.checkSymbol(),
      isValid: this.getValidator().isValid(inputValue)
    });

    if (inputPlan.kind === 'empty') {
      // Control -> owner: the user cleared the field, so (unlike `resetCachedValues()`,
      // which is a silent owner-facing reset) this must echo `null` back to the owning
      // form via `setModel()`, otherwise a previously-set invalid value (e.g. a
      // duplicate-symbol match) keeps failing validation forever.
      this.setModel(null);
      this.symbols.set([]);
      this.updateValidity();
      this.symbolInputValueChange.next(inputPlan.emitQuery);
      this.finishInputChange(inputValue, false);
      return;
    }

    this.querySymbols(inputPlan.queryValue);
    this.applyInputModelEffects(inputPlan.modelEffects, inputValue);
    this.finishInputChange(inputValue, true);
  }

  inputBlur(): void {
    this.focusOnInput.set(false);
    this.markTouched();
  }

  // either from lost focus or button clicked.
  // input value might not be correct
  onSymbolButtonClicked(): void {
    const clickPlan = planSymbolLookupButtonClick(this.disabled(), !this.isEmptyInputValue(this.symbolInput()), this.symbols().length > 0);

    if (clickPlan.shouldCheckSymbol) {
      this.checkSymbol.set(true);
    }

    switch (clickPlan.kind) {
      case 'validateAndSelect':
        this.validateAndInvokeOnSelect('button');
        break;
      case 'markTouched':
        this.markTouched();
        break;
      default:
        break;
    }

    if (clickPlan.shouldUpdateValidity) {
      this.updateValidity();
    }
  }

  updateValidity(): void {
    this.bridgeService.revalidateCurrentControl(this.resolveNgControl());
  }

  isControlInvalid(): boolean {
    return this.bridgeService.isCurrentControlInvalid(this.resolveNgControl());
  }

  // selection happened from the dropdown, always trust it
  onSelectSymbol(selection: { selected: SecuritySymbol | AutoSuggestSymbol }, from: string): void {
    if (this.disabled()) {
      return;
    }

    if (from !== undefined && selection !== undefined) {
      this.setModel(selection.selected, true);
      this.checkSymbol.set(true);
      this.markTouched();
      this.onSelect.emit(selection.selected as SecurityInfo);
      this.symbols.set([]);
    }
  }

  /**
   * Owner -> control. Must never echo back through `onChangeCallback` (§4.2 criterion 4),
   * and normalizes empty to `null` (§4.2 criterion 5).
   */
  writeValue(value: T | null | undefined): void {
    this.value.set(value ?? null);

    const inputValue = this.symbolToString(value);
    if (this.symbolInput() !== inputValue) {
      this.symbolInput.set(inputValue);
      if (!this.isEmptyInputValue(inputValue) && this.getValidator().isValid(inputValue)) {
        this.checkSymbol.set(true);
      }
    }
  }

  /**
   * Reset previously retrieved values.
   *
   * This is a programmatic reset, so it deliberately does not notify the form owner:
   * `writeValue()` is an owner-to-control write and must never echo back through onChange.
   */
  resetCachedValues(): void {
    this.writeValue(null);
    this.symbols.set([]);
    this.updateValidity();
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByOwner.set(isDisabled);
  }

  private symbolToString(value: T): string {
    if (value === undefined || value === null) {
      return '';
    }
    return SymbolLookupService.symbolToString(value);
  }

  private setupSymbolInputValueChangeObserver(): void {
    this.symbolInputValueChange
      .pipe(
        debounceTime(this.debounce()),
        switchMap((symbolQuery: string): Observable<Array<AutoSuggestSymbol>> => resolveSymbolLookupSearch({
          symbolQuery,
          isRecentSearch: this.isRecentSearch(),
          currentSymbols: this.symbols(),
          search: (): Observable<Array<AutoSuggestSymbol>> => this.doSearch(symbolQuery)
        })),
        takeUntilDestroyed()
      )
      .subscribe({
        next: (result: Array<AutoSuggestSymbol>): void => {
          this.symbols.set(result);
          if (this.symbolInput() !== '') {
            this.isRecentSearch.set(false);
          }
        }
        // no error handler is needed here, as it's handled in the stream now
      });
  }

  private doSearch(symbolQuery: string): Observable<Array<AutoSuggestSymbol>> {
    return this.getActiveDataSource().search<AutoSuggestSymbol>(
      symbolQuery,
      this.currentMaxResult,
      this.symbolTypes(),
      this.market(),
      this.fractionalSymbolSearch(),
      this.filterBy()
    );
  }

  private getRecentSearches(): Observable<Array<T>> {
    return this.getActiveDataSource().getRecentSymbols(this.currentMaxResult, this.symbolTypes(), this.market());
  }

  private getActiveDataSource(): SymbolLookupDataSource {
    return this.dataSource() ?? this.defaultDataSource;
  }

  private setModel(symbol: SecuritySymbol | AutoSuggestSymbol | null | undefined, overwrite: boolean = true): void {
    const currentValue = this.value();
    const nextValue = resolveSymbolLookupModelValue<T>({
      currentValue,
      nextSymbol: symbol,
      overwrite
    });

    if (currentValue !== nextValue) {
      this.value.set(nextValue);
      this.onChangeCallback(nextValue);
    }
  }

  private isEmptyInputValue(inputValue: string | null | undefined): boolean {
    return inputValue === null || inputValue === undefined || inputValue === '';
  }

  private validateAndInvokeOnSelect(source: string): void {
    if (this.fractionalSymbolSearch()) {
      const exactSelection = this.resolveExactSelection(this.symbolInput(), this.symbols());
      if (exactSelection !== undefined) {
        this.onSelectSymbol({ selected: exactSelection }, source);
      } else {
        // Clearing the model is sufficient: `SymbolLookupValidatorDirective` reports
        // `{ invalid: true }` for a non-empty input with no resolved symbol, and the
        // caller (`onSymbolButtonClicked`) revalidates immediately afterwards. Setting
        // errors imperatively on NgControl would be overwritten by that revalidation,
        // and throws under Signal Forms, whose InteropNgControl has no `setErrors`.
        this.setModel(null);
        this.markTouched();
      }
      return;
    }

    const selectionPlan = planSymbolLookupSelection({
      inputValue: this.symbolInput(),
      symbols: this.symbols(),
      isValid: this.getValidator().isValid(this.symbolInput()),
      lenient: this.lenient()
    });

    switch (selectionPlan.kind) {
      case 'setModelThenSelect':
        this.setModel(selectionPlan.nextModel);
        this.onSelectSymbol({ selected: selectionPlan.selected }, source);
        break;
      case 'select':
        this.onSelectSymbol({ selected: selectionPlan.selected }, source);
        break;
      default:
        break;
    }
  }

  private resolveExactSelection(
    inputValue: string | null | undefined,
    candidates: Array<AutoSuggestSymbol>
  ): AutoSuggestSymbol | undefined {
    if (inputValue === undefined || inputValue === null || inputValue === '') {
      return undefined;
    }

    const parsedInput = SecuritySymbolHelper.parse<SecuritySymbol>(inputValue);
    const inputSymbol = parsedInput?.symbol?.toUpperCase() ?? '';
    const inputMarket = parsedInput?.marketType?.toUpperCase() ?? '';

    if (inputSymbol === '') {
      return undefined;
    }

    const symbolMatches = candidates.filter((candidate: AutoSuggestSymbol): boolean =>
      (candidate.symbol ?? '').toUpperCase() === inputSymbol
    );

    if (symbolMatches.length === 0) {
      return undefined;
    }

    if (inputMarket !== '') {
      return symbolMatches.find((candidate: AutoSuggestSymbol): boolean =>
        (candidate.marketType ?? '').toUpperCase() === inputMarket
      );
    }

    return symbolMatches[0];
  }

  /**
   * Get a validator with current component configuration.
   * This is called on-demand to ensure it reflects the latest input() signal values.
   */
  private getValidator(): SymbolValidator {
    return new SymbolValidator(this.market(), this.symbolTypes());
  }

  private shouldAutoSelectOnFocusOut(event: FocusEvent): boolean {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    return shouldAutoSelectOnFocusOut({
      isValidSymbol: this.getValidator().isValid(this.symbolInput()),
      checkSymbol: this.checkSymbol(),
      targetId: target.id,
      targetType: target.getAttribute('type'),
      relatedTarget: event.relatedTarget
    });
  }

  private emitHoverOrActiveState(): void {
    this.onHoverOrActive.emit({ hoverState: this.hoverState(), focusState: this.focusState() });
  }

  private setFocusState(isFocused: boolean): void {
    this.focusState.set(isFocused);
    this.emitHoverOrActiveState();
  }

  private setHoverState(isHovered: boolean): void {
    this.hoverState.set(isHovered);
    this.emitHoverOrActiveState();
  }

  private applyInputModelEffects(modelEffects: ReadonlyArray<SymbolLookupInputModelEffect>, inputValue: string): void {
    for (const modelEffect of modelEffects) {
      if (modelEffect === 'setLenientModel') {
        this.setModel({ symbol: inputValue });
        continue;
      }

      this.setModel(null);
      this.checkSymbol.set(false);
    }
  }

  private finishInputChange(inputValue: string, shouldUpdateValidity: boolean): void {
    this.onInputChange.emit(inputValue);
    if (shouldUpdateValidity) {
      this.updateValidity();
    }
  }

  private markTouched(): void {
    this.onTouchedCallback();
    this.touch.emit();
  }

  private resolveNgControl(): NgControl | null {
    // Resolved lazily: injecting NgControl eagerly while this component is also
    // registered as its own NG_VALUE_ACCESSOR creates an NG0200 circular dependency.
    if (!this.isNgControlResolved) {
      this.ngControlRef = this.bridgeService.getNgControl(this.injector);
      this.isNgControlResolved = true;
    }

    return this.ngControlRef;
  }
}
